import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../utils/axios-auth';
import { getUserFacingMessageFromAxiosError } from '../utils/user-facing-api-error';
// Default badges
const defaultBadges = [
    {
        id: 'quiz_master',
        name: 'Quiz Master',
        description: 'Complete 10 quizzes',
        icon: '🎯',
        category: 'quiz',
        rarity: 'common',
        requirements: {
            type: 'quiz_completion',
            value: 10
        },
        max_progress: 10
    },
    {
        id: 'streak_warrior',
        name: 'Streak Warrior',
        description: 'Maintain a 7-day learning streak',
        icon: '🔥',
        category: 'streak',
        rarity: 'rare',
        requirements: {
            type: 'streak_days',
            value: 7
        },
        max_progress: 7
    },
    {
        id: 'engagement_champion',
        name: 'Engagement Champion',
        description: 'Achieve high engagement level 5 times',
        icon: '⭐',
        category: 'engagement',
        rarity: 'epic',
        requirements: {
            type: 'engagement_level',
            value: 5
        },
        max_progress: 5
    },
    {
        id: 'perfect_score',
        name: 'Perfect Score',
        description: 'Score 100% on a quiz',
        icon: '🏆',
        category: 'achievement',
        rarity: 'legendary',
        requirements: {
            type: 'score_threshold',
            value: 100
        },
        max_progress: 1
    },
    {
        id: 'lesson_explorer',
        name: 'Lesson Explorer',
        description: 'Complete 5 lessons',
        icon: '📚',
        category: 'lesson',
        rarity: 'common',
        requirements: {
            type: 'lesson_completion',
            value: 5
        },
        max_progress: 5
    },
    {
        id: 'marathon_learner',
        name: 'Marathon Learner',
        description: 'Maintain a 30-day learning streak',
        icon: '🏃‍♂️',
        category: 'streak',
        rarity: 'legendary',
        requirements: {
            type: 'streak_days',
            value: 30
        },
        max_progress: 30
    }
];
// Async thunks
export const fetchUserGamificationData = createAsyncThunk('gamification/fetchUserData', async (userId, { rejectWithValue }) => {
    try {
        const useCurrentUser = userId === undefined || userId === 'me';
        const response = useCurrentUser
            ? await axiosInstance.get('/gamification/me')
            : await axiosInstance.get(`/gamification/user/${userId}`);
        if (response.data?.success && response.data?.data) {
            return response.data.data;
        }
        return response.data?.data ?? response.data;
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to fetch gamification data' }));
    }
});
export const updateStreak = createAsyncThunk('gamification/updateStreak', async ({ userId, streakType, activity }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/gamification/streak`, {
            user_id: userId,
            streak_type: streakType,
            activity
        });
        return response.data;
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to update streak' }));
    }
});
export const awardBadge = createAsyncThunk('gamification/awardBadge', async ({ userId, badgeId, progress }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/gamification/badge`, {
            user_id: userId,
            badge_id: badgeId,
            progress
        });
        return response.data;
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to award badge' }));
    }
});
export const addExperiencePoints = createAsyncThunk('gamification/addExperience', async ({ userId, points, activity }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/gamification/experience`, {
            user_id: userId,
            points,
            activity
        });
        return response.data;
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to add experience points' }));
    }
});
// Initial state
const initialState = {
    badges: defaultBadges,
    earned_badges: [],
    streaks: [
        {
            id: 'daily_login',
            type: 'daily_login',
            current_count: 0,
            max_count: 0,
            last_activity_date: '',
            is_active: false,
            started_at: ''
        },
        {
            id: 'quiz_streak',
            type: 'quiz_completion',
            current_count: 0,
            max_count: 0,
            last_activity_date: '',
            is_active: false,
            started_at: ''
        },
        {
            id: 'lesson_streak',
            type: 'lesson_completion',
            current_count: 0,
            max_count: 0,
            last_activity_date: '',
            is_active: false,
            started_at: ''
        }
    ],
    total_points: 0,
    level: 1,
    experience_points: 0,
    next_level_threshold: 100,
    status: 'idle',
    error: null
};
// Helper functions
const calculateLevel = (xp) => {
    return Math.floor(Math.sqrt(xp / 100)) + 1;
};
const calculateNextLevelThreshold = (level) => {
    return level * level * 100;
};
function normalizeStreakType(rawType) {
    switch (rawType) {
        case 'daily_login':
            return 'daily_login';
        case 'quiz_completion':
        case 'quiz_streak':
            return 'quiz_completion';
        case 'lesson_completion':
        case 'lesson_streak':
            return 'lesson_completion';
        case 'engagement':
            return 'engagement';
        case 'lesson_cycle':
            return 'lesson_cycle';
        default:
            return 'daily_login';
    }
}
function normalizeGamificationPayload(payload, fallbackStreaks) {
    const earnedBadges = Array.isArray(payload?.earned_badges)
        ? payload.earned_badges.map((entry) => {
            const badge = entry?.badge || {};
            return {
                id: entry?.id || `${entry?.badge_id || 'badge'}_${Date.now()}`,
                student_id: entry?.student_id || entry?.user_id || payload?.user_id || '',
                badge_id: entry?.badge_id || '',
                earned_at: entry?.earned_at || new Date().toISOString(),
                progress: typeof entry?.progress === 'number' ? entry.progress : 0,
                metadata: {
                    ...(entry?.metadata || {}),
                    badge,
                    title: badge?.name || entry?.title || entry?.name || 'Badge',
                    description: badge?.description || entry?.description || '',
                    icon: badge?.icon || badge?.icon_url || entry?.icon || '⭐',
                    rarity: entry?.rarity || 'common'
                }
            };
        })
        : [];
    const streaks = Array.isArray(payload?.streaks)
        ? payload.streaks.map((entry) => {
            const currentCount = typeof entry?.current_count === 'number'
                ? entry.current_count
                : (typeof entry?.current_streak === 'number' ? entry.current_streak : 0);
            const maxCount = typeof entry?.max_count === 'number'
                ? entry.max_count
                : (typeof entry?.longest_streak === 'number' ? entry.longest_streak : currentCount);
            const lastActivity = entry?.last_activity_date || '';
            const normalizedType = normalizeStreakType(entry?.type || entry?.streak_type || '');
            return {
                id: entry?.id || `${normalizedType}_${Date.now()}`,
                type: normalizedType,
                current_count: currentCount,
                max_count: maxCount,
                last_activity_date: lastActivity,
                is_active: typeof entry?.is_active === 'boolean' ? entry.is_active : currentCount > 0,
                started_at: entry?.started_at || lastActivity || new Date().toISOString()
            };
        })
        : fallbackStreaks;
    return { earnedBadges, streaks };
}
// Gamification slice
const gamificationSlice = createSlice({
    name: 'gamification',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        // Local badge progress update
        updateBadgeProgress: (state, action) => {
            const { badgeId, progress } = action.payload;
            const existingBadge = state.earned_badges.find(b => b.badge_id === badgeId);
            if (existingBadge) {
                existingBadge.progress = progress;
            }
            else {
                // Check if badge can be earned
                const badge = state.badges.find(b => b.id === badgeId);
                if (badge && progress >= badge.requirements.value) {
                    state.earned_badges.push({
                        id: `${badgeId}_${Date.now()}`,
                        student_id: '', // Will be set from user context
                        badge_id: badgeId,
                        earned_at: new Date().toISOString(),
                        progress: progress
                    });
                }
            }
        },
        // Local streak update
        updateStreakLocal: (state, action) => {
            const { streakType, increment } = action.payload;
            const streak = state.streaks.find(s => s.type === streakType);
            if (streak) {
                const today = new Date().toISOString().split('T')[0];
                const lastActivity = streak.last_activity_date.split('T')[0];
                if (increment) {
                    // Check if it's a new day
                    if (lastActivity !== today) {
                        // Check if streak continues (yesterday) or breaks
                        const yesterday = new Date();
                        yesterday.setDate(yesterday.getDate() - 1);
                        const yesterdayStr = yesterday.toISOString().split('T')[0];
                        if (lastActivity === yesterdayStr || streak.current_count === 0) {
                            // Continue streak
                            streak.current_count += 1;
                            streak.max_count = Math.max(streak.max_count, streak.current_count);
                            streak.is_active = true;
                        }
                        else {
                            // Break streak, start new one
                            streak.current_count = 1;
                            streak.started_at = today;
                        }
                        streak.last_activity_date = new Date().toISOString();
                    }
                }
            }
        },
        // Add experience points locally
        addExperienceLocal: (state, action) => {
            const points = action.payload;
            state.experience_points += points;
            state.total_points += points;
            // Check for level up
            const newLevel = calculateLevel(state.experience_points);
            if (newLevel > state.level) {
                state.level = newLevel;
                state.next_level_threshold = calculateNextLevelThreshold(newLevel);
            }
        },
        // Initialize streaks for new user
        initializeStreaks: (state) => {
            const today = new Date().toISOString();
            state.streaks.forEach(streak => {
                if (!streak.started_at) {
                    streak.started_at = today;
                    streak.last_activity_date = today;
                }
            });
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch user gamification data
            .addCase(fetchUserGamificationData.pending, (state) => {
            state.status = 'loading';
        })
            .addCase(fetchUserGamificationData.fulfilled, (state, action) => {
            state.status = 'succeeded';
            if (action.payload) {
                const normalized = normalizeGamificationPayload(action.payload, state.streaks);
                state.earned_badges = normalized.earnedBadges;
                state.streaks = normalized.streaks;
                state.total_points = action.payload.total_points || 0;
                state.level = action.payload.level || 1;
                state.experience_points = action.payload.experience_points || 0;
                state.next_level_threshold = action.payload.next_level_threshold || 100;
            }
            state.error = null;
        })
            .addCase(fetchUserGamificationData.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload;
        })
            // Update streak
            .addCase(updateStreak.fulfilled, (state, action) => {
            if (action.payload) {
                const updatedStreak = action.payload;
                const index = state.streaks.findIndex(s => s.id === updatedStreak.id);
                if (index !== -1) {
                    state.streaks[index] = updatedStreak;
                }
            }
        })
            // Award badge
            .addCase(awardBadge.fulfilled, (state, action) => {
            if (action.payload) {
                const newBadge = action.payload;
                const existingIndex = state.earned_badges.findIndex(b => b.badge_id === newBadge.badge_id);
                if (existingIndex !== -1) {
                    state.earned_badges[existingIndex] = newBadge;
                }
                else {
                    state.earned_badges.push(newBadge);
                }
            }
        })
            // Add experience points
            .addCase(addExperiencePoints.fulfilled, (state, action) => {
            if (action.payload) {
                state.experience_points = action.payload.experience_points;
                state.total_points = action.payload.total_points;
                state.level = action.payload.level;
                state.next_level_threshold = action.payload.next_level_threshold;
            }
        });
    }
});
export const { clearError, updateBadgeProgress, updateStreakLocal, addExperienceLocal, initializeStreaks } = gamificationSlice.actions;
// Selectors
export const selectBadges = (state) => state.gamification.badges;
export const selectEarnedBadges = (state) => state.gamification.earned_badges;
export const selectStreaks = (state) => state.gamification.streaks;
export const selectUserLevel = (state) => state.gamification.level;
export const selectExperiencePoints = (state) => state.gamification.experience_points;
export const selectTotalPoints = (state) => state.gamification.total_points;
export const selectNextLevelThreshold = (state) => state.gamification.next_level_threshold;
export default gamificationSlice.reducer;
